import { AddIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
// barrel 経由だとチャート系が react-virtualized の ESM build を引き込み、
// jest が変換できなくなるため直接読み込みます。
import { CmStack } from '@/components/uiParts/Layout/Stack/CmStack';
import {
  csvExportFiles,
  RELATION_KIND_STYLE,
} from '@/pages/externals/pos-menu-masters/service';

import { MenuMasterPagination } from './components/MenuMasterPagination';
import { MenuMasterTable } from './components/MenuMasterTable';
import { MenuMasterToolbar } from './components/MenuMasterToolbar';
import { SummaryCards } from './components/SummaryCards';
import { downloadCsvFiles } from './csvDownload';
import { useMenuMasterListView } from './useMenuMasterListView';
import { useMosPosMenuMasterListPage } from './useMosPosMenuMasterListPage';

const MosPosMenuMasterListPage: React.FC = () => {
  const {
    importResult,
    businessDate,
    summary,
    menus,
    isLoading,
    error,
    schemaMigrationRequired,
  } = useMosPosMenuMasterListPage();

  const view = useMenuMasterListView(menus);

  const exportCsv = () =>
    downloadCsvFiles(csvExportFiles(view.filtered, businessDate, menus));

  return (
    <PageLayout title="POSメニューマスタ">
      <CmStack spacing={4} my={5}>
        <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
          <Flex direction="column" gap={1}>
            <Text fontSize="md" fontWeight="bold">
              登録済みPOSメニューマスタ
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
              data-testid="list-page-description"
            >
              登録済みのPOSメニューマスタを表示します（本画面は参照のみ。登録・更新はCSVインポートから行います）
            </Text>
          </Flex>
          <Button
            as={RouterLink}
            to="/externals/pos-menu-masters/import"
            colorScheme="darkBlue"
            size="sm"
            leftIcon={<AddIcon data-testid="list-import-icon" />}
            iconSpacing={2}
          >
            CSVインポート
          </Button>
        </Flex>

        {importResult && (
          <Alert status="success" data-testid="import-success">
            <AlertIcon />
            登録が完了しました（新規 {importResult.new}件・更新{' '}
            {importResult.update}件）。
          </Alert>
        )}

        {schemaMigrationRequired && (
          <Alert status="error" data-testid="migration-required">
            <AlertIcon />
            メニューマスタのデータ移行が完了していないため表示できません。
            移行の完了後に再度お試しください。
          </Alert>
        )}

        {!schemaMigrationRequired && error && (
          <Alert status="error" data-testid="list-error">
            <AlertIcon />
            メニューマスタの取得に失敗しました。時間をおいて再度お試しください。
          </Alert>
        )}

        {isLoading && <Spinner data-testid="list-loading" />}

        {!isLoading && !error && summary && (
          <>
            <SummaryCards summary={summary} />

            <Box
              borderWidth="1px"
              borderRadius="5px"
              bg="white"
              boxShadow="0 1px 2px rgba(45,55,72,0.04)"
            >
              <Flex
                justify="space-between"
                align="center"
                gap={3}
                wrap="wrap"
                px={5}
                py={4}
                borderBottomWidth="1px"
                borderColor="gray.200"
                data-testid="list-card-head"
                data-layout="card-header"
              >
                <Text fontSize="sm" fontWeight="bold">
                  マスタ一覧
                </Text>
                <Text fontSize="xs" color="gray.500">
                  親商品をクリックすると子商品を展開して確認できます
                </Text>
              </Flex>

              <Box px={5} py={4}>
                <MenuMasterToolbar
                  keyword={view.keyword}
                  filter={view.filter}
                  shown={view.pageMenus.length}
                  total={view.filtered.length}
                  onKeyword={view.changeKeyword}
                  onFilter={view.changeFilter}
                  onExport={exportCsv}
                />

                <MenuMasterTable
                  menus={view.pageMenus}
                  businessDate={businessDate}
                />

                <MenuMasterPagination
                  page={view.page}
                  totalPages={view.totalPages}
                  pageSize={view.pageSize}
                  total={view.filtered.length}
                  onPage={view.setPage}
                  onPageSize={view.changePageSize}
                />

                <Text
                  as="div"
                  fontSize="xs"
                  color="gray.600"
                  mt={3.5}
                  data-testid="list-legend"
                >
                  <Text as="span" fontWeight="bold">
                    凡例
                  </Text>{' '}
                  —{' '}
                  <Badge
                    {...RELATION_KIND_STYLE.included}
                    fontSize="10px"
                    fontWeight="extrabold"
                    letterSpacing="0.05em"
                    px={2}
                    py={0.5}
                    verticalAlign="middle"
                    data-testid="list-legend-set"
                  >
                    構成品
                  </Badge>{' '}
                  セットに必ず含まれる商品／{' '}
                  <Badge
                    {...RELATION_KIND_STYLE.choice}
                    fontSize="10px"
                    fontWeight="extrabold"
                    letterSpacing="0.05em"
                    px={2}
                    py={0.5}
                    verticalAlign="middle"
                    data-testid="list-legend-choice"
                  >
                    チョイス
                  </Badge>{' '}
                  セット内でお客様が1つ選ぶ商品／{' '}
                  <Badge
                    {...RELATION_KIND_STYLE.accessory}
                    fontSize="10px"
                    fontWeight="extrabold"
                    letterSpacing="0.05em"
                    px={2}
                    py={0.5}
                    verticalAlign="middle"
                    data-testid="list-legend-accessory"
                  >
                    付属
                  </Badge>{' '}
                  親商品に自動で付くソース等。
                </Text>
              </Box>
            </Box>
          </>
        )}
      </CmStack>
    </PageLayout>
  );
};

export default MosPosMenuMasterListPage;
